import React, { useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ENV, isDevelopment, debugLog } from '../config/environment';
import { AuthContext } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  UserGroupIcon,
  CogIcon,
  UserCircleIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  DocumentChartBarIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Disclosure, Menu, Transition } from '@headlessui/react';

const Header = () => {
  const location = useLocation();
  const { user, personas, activePersona, switchPersona } = useContext(AuthContext);
  const { canAccessAgentFeatures, canAccessAdminFeatures, navigation } = usePermissions();

  // Log navigation for debugging
  debugLog('Current location:', location.pathname);
  debugLog('User permissions:', { canAccessAgentFeatures, canAccessAdminFeatures, navigation });

  // Primary navigation - Agent features (accessible by Agents and System Admins)
  const primaryNavItems = [
    { name: 'Properties', href: '/properties', icon: BuildingOfficeIcon, requiresAgent: true },
    { name: 'Collections', href: '/bankList', icon: BanknotesIcon, requiresAgent: true },
    { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon, requiresAgent: true },
  ];

  // Secondary navigation - in dropdown
  const secondaryNavItems = [
    { name: 'Payment Validation', href: '/payment-validation', icon: ShieldCheckIcon, requiresAgent: true },
    { name: 'Agencies', href: '/agencies', icon: BuildingOfficeIcon, requiresAdmin: true },
    { name: 'Occupants', href: '/occupants', icon: UserGroupIcon, requiresAgent: true },
    { name: 'User Management', href: '/admin/users', icon: UserGroupIcon, requiresAdmin: true },
    { name: 'Role Management', href: '/admin/roles', icon: CogIcon, requiresAdmin: true },
    { name: 'Admin', href: '/admin', icon: CogIcon, requiresAdmin: true },
  ];

  // Filter navigation items based on permissions
  const primaryNav = useMemo(() => {
    return primaryNavItems.filter(item => {
      if (item.requiresAdmin) return canAccessAdminFeatures;
      if (item.requiresAgent) return canAccessAgentFeatures;
      return true;
    });
  }, [canAccessAgentFeatures, canAccessAdminFeatures]);

  const secondaryNav = useMemo(() => {
    return secondaryNavItems.filter(item => {
      if (item.requiresAdmin) return canAccessAdminFeatures;
      if (item.requiresAgent) return canAccessAgentFeatures;
      return true;
    });
  }, [canAccessAgentFeatures, canAccessAdminFeatures]);

  // Check if there are any secondary nav items to show the "More" dropdown
  const hasSecondaryNav = secondaryNav.length > 0;

  const handleLogout = () => {
    window.location.href = '/login';
  };

  const handleSwitchPersona = async (personaId) => {
    try {
      await switchPersona(personaId);
      // Reload to refresh all data with new entity context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch persona:', error);
      alert('Failed to switch entity. Please try again.');
    }
  };

  const isActivePath = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <Disclosure as="nav" className="bg-white border-b border-gray-200">
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              {/* Left section - Logo & Primary Nav */}
              <div className="flex items-center">
                <Link to="/" className="flex-shrink-0 flex items-center group">
                  <div className="bg-emerald-500 p-2 rounded-lg group-hover:bg-emerald-600 transition-colors">
                    <HomeIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="ml-3 text-lg font-semibold text-gray-900">{ENV.APP_NAME}</span>
                  {isDevelopment() && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                      DEV
                    </span>
                  )}
                </Link>

                {/* Primary Navigation */}
                <div className="hidden md:flex items-center ml-8 space-x-1">
                  {primaryNav.map((item) => {
                    const isActive = isActivePath(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors
                          ${isActive 
                            ? 'text-emerald-600 bg-emerald-50' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }
                        `}
                      >
                        <item.icon className="h-4 w-4 mr-2" />
                        {item.name}
                      </Link>
                    );
                  })}

                  {/* More Dropdown - only show if there are secondary nav items */}
                  {hasSecondaryNav && (
                    <Menu as="div" className="relative">
                      <Menu.Button className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors">
                        More
                        <ChevronDownIcon className="ml-1 h-4 w-4" />
                      </Menu.Button>
                      <Transition
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute left-0 z-20 mt-2 w-48 origin-top-left bg-white rounded-lg shadow-lg ring-1 ring-gray-200 focus:outline-none py-1">
                          {secondaryNav.map((item) => (
                            <Menu.Item key={item.name}>
                              {({ active }) => (
                                <Link
                                  to={item.href}
                                  className={`
                                    flex items-center px-4 py-2 text-sm
                                    ${isActivePath(item.href) ? 'text-emerald-600 bg-emerald-50' : ''}
                                    ${active && !isActivePath(item.href) ? 'bg-gray-50 text-gray-900' : 'text-gray-700'}
                                  `}
                                >
                                  <item.icon className="h-4 w-4 mr-3 text-gray-400" />
                                  {item.name}
                                </Link>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  )}
                </div>
              </div>

              {/* Right section - Persona Switcher & User Menu */}
              <div className="hidden md:flex items-center space-x-3">
                {/* Persona Switcher - only show if multiple personas */}
                {personas && personas.length > 1 && (
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-500" />
                      <span className="max-w-[150px] truncate">
                        {activePersona?.name || 'Select Entity'}
                      </span>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </Menu.Button>
                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-20 mt-2 w-72 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-gray-200 focus:outline-none py-1">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Switch Entity
                          </p>
                        </div>
                        {personas.map((persona) => {
                          const isActive = persona.persona_id === activePersona?.persona_id;
                          return (
                            <Menu.Item key={persona.persona_id}>
                              {({ active }) => (
                                <button
                                  onClick={() => !isActive && handleSwitchPersona(persona.persona_id)}
                                  className={`
                                    w-full text-left px-4 py-3 text-sm transition-colors
                                    ${isActive ? 'bg-emerald-50 cursor-default' : ''}
                                    ${active && !isActive ? 'bg-gray-50' : ''}
                                  `}
                                  disabled={isActive}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className={`font-medium truncate ${isActive ? 'text-emerald-600' : 'text-gray-900'}`}>
                                        {persona.name}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {persona.type === 'agency' ? 'Agency' : persona.type === 'owner' ? 'Owner' : 'Head Tenant'} • {persona.role_name}
                                      </p>
                                      <p className="text-xs text-gray-400 mt-0.5">
                                        {persona.country} • {persona.currency}
                                      </p>
                                    </div>
                                    {isActive && (
                                      <div className="ml-2 flex-shrink-0">
                                        <div className="h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
                                          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                                            <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z"/>
                                          </svg>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </button>
                              )}
                            </Menu.Item>
                          );
                        })}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}

                {/* User Menu */}
                {user ? (
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center space-x-3 p-1.5 rounded-full hover:bg-gray-100 transition-colors">
                      <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {(user?.username || user?.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="hidden lg:block text-left">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.username || user?.email || 'User'}
                        </p>
                        <p className="text-xs text-gray-500">Account</p>
                      </div>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </Menu.Button>
                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-20 mt-2 w-56 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-gray-200 focus:outline-none divide-y divide-gray-100">
                        <div className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{user?.username || 'User'}</p>
                          <p className="text-xs text-gray-500 truncate">{user?.email || 'user@example.com'}</p>
                        </div>
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/profile"
                                className={`flex items-center px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
                              >
                                <UserCircleIcon className="h-4 w-4 mr-3 text-gray-400" />
                                Your Profile
                              </Link>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/admin"
                                className={`flex items-center px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
                              >
                                <CogIcon className="h-4 w-4 mr-3 text-gray-400" />
                                Settings
                              </Link>
                            )}
                          </Menu.Item>
                        </div>
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={handleLogout}
                                className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
                              >
                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-gray-400" />
                                Sign out
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                ) : (
                  <Link
                    to="/login"
                    className="bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Sign in
                  </Link>
                )}
              </div>

              {/* Mobile menu button */}
              <div className="flex md:hidden">
                <Disclosure.Button className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                  <span className="sr-only">Open menu</span>
                  {open ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <Disclosure.Panel className="md:hidden border-t border-gray-200">
            {/* Persona Switcher - Mobile */}
            {personas && personas.length > 1 && (
              <div className="px-4 pt-3 pb-2 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Switch Entity
                </p>
                <div className="space-y-1">
                  {personas.map((persona) => {
                    const isActive = persona.persona_id === activePersona?.persona_id;
                    return (
                      <button
                        key={persona.persona_id}
                        onClick={() => !isActive && handleSwitchPersona(persona.persona_id)}
                        disabled={isActive}
                        className={`
                          w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors
                          ${isActive ? 'bg-emerald-50 border-2 border-emerald-500' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'}
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isActive ? 'text-emerald-600' : 'text-gray-900'}`}>
                              {persona.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {persona.type === 'agency' ? 'Agency' : persona.type === 'owner' ? 'Owner' : 'Head Tenant'} • {persona.role_name}
                            </p>
                          </div>
                          {isActive && (
                            <div className="ml-2 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                              <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="px-4 pt-2 pb-3 space-y-1">
              {[...primaryNav, ...secondaryNav].map((item) => {
                const isActive = isActivePath(item.href);
                return (
                  <Disclosure.Button
                    key={item.name}
                    as={Link}
                    to={item.href}
                    className={`
                      flex items-center w-full px-3 py-2.5 text-base font-medium rounded-lg transition-colors
                      ${isActive 
                        ? 'text-emerald-600 bg-emerald-50' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }
                    `}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </Disclosure.Button>
                );
              })}
            </div>
            {user && (
              <div className="border-t border-gray-200 px-4 py-4">
                <div className="flex items-center mb-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {(user?.username || user?.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{user?.username || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.email || 'user@example.com'}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Disclosure.Button
                    as={Link}
                    to="/profile"
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                  >
                    <UserCircleIcon className="h-5 w-5 mr-3" />
                    Your Profile
                  </Disclosure.Button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default Header;
